import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AgendapuntService, IAgendapuntViewResult, IBoaActivity } from '../agendapunt.service';
import { IDocument } from '../../detail-zitting/detail-zitting.service';
import { mergeMap, filter, map, withLatestFrom, switchMap, delay } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { jumpToAnchor } from '../../shared/helpers';
import { MenuService } from '../../shared/menu.service';
import { IAgendapuntDb, IDetailAgendapuntDb, IDossierDb, IReferencedPuntDb, INotitieDb, IDocumentDb, ISubpuntDb } from '../../shared/offline/cobradb';
import { IAgendapuntStandpuntInfo, StandpuntService } from '../../standpunten/standpunt.service';
import { AppService } from '../../shared/app.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { createCmdFactory } from '@cevi/rxjscommands';
import { state2 } from 'src/app/shared/state';
import { BepaalStandpuntComponent } from '../../standpunten/bepaal-standpunt/bepaal-standpunt.component';
import { NgIf, NgClass, NgFor, AsyncPipe, DatePipe } from '@angular/common';

interface IDetailPuntVm {
  agendapunt: IAgendapuntDb;
  detail: IDetailAgendapuntDb;
  agendapuntIds: number[];
  streamingLink: SafeResourceUrl;
  mediaActive: boolean;
  bespreken: boolean;
  id: number;
  zittingid: number;
  standpunten?: IAgendapuntStandpuntInfo[];
  boaActivity?: IBoaActivity;
}

const emptyVm: IDetailPuntVm = {
  agendapunt: {} as IAgendapuntDb,
  detail: {
    Subpunten: [],
    Documenten: [],
    GekoppeldePunten: [],
    Notities: [],
    Dossiers: [],
    StreamingLink: ''
  } as IDetailAgendapuntDb,
  agendapuntIds: [],
  streamingLink: '',
  mediaActive: false,
  bespreken: false,
  id: 0,
  zittingid: 0
};

@Component({
    selector: 'app-detail-agendapunt',
    templateUrl: './detail-agendapunt.component.html',
    styleUrls: ['./detail-agendapunt.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgIf, RouterLink, NgClass, NgFor, BepaalStandpuntComponent, AsyncPipe, DatePipe],
    providers:[StandpuntService]
})
export class DetailAgendapunt2Component implements OnInit /*, OnDestroy*/ {



  vm$: Observable<IDetailPuntVm>;
  vm: IDetailPuntVm;

  toggleBookmarkSubj = new Subject<IAgendapuntDb>();
  puntNavigationSubj = new Subject<number>();
  activateMediaSubj = new Subject<boolean>();
  verwijderNotitieSubj = new Subject<INotitieDb>();
  closeStandpuntSubj = new Subject<IDetailPuntVm>();
  bepaalStandpuntSubj = new Subject<IAgendapuntDb>();
  getBoaActivitySubj = new Subject<string>();

  constructor(private menuService: MenuService,
    private agendapuntService: AgendapuntService,
    private standpuntService: StandpuntService,
    public appService: AppService,
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer) {

    appService.routing.createCurrentHistoryItem('Agendapunt');

    const cmdFactory = createCmdFactory<IDetailPuntVm>();
    cmdFactory.addMapAction(this.refreshDataFromAppServiceAction$, 'refreshFromAppService');
    cmdFactory.addMapAction(this.retrieveAgendapuntDataAction$, 'retrieveAgendapuntData');
    cmdFactory.addMapAction(this.openDocFromDocIdParamAction$, 'openDocFromDocIdParam');
    cmdFactory.addMapAction(this.jumpToAnchorFromFragmentAction$, 'jumpToAnchor');
    cmdFactory.addMapAction(this.toggleBookmarkAction$, 'toggleBookmark');
    cmdFactory.addMapAction(this.puntNavigationAction$, 'puntnavigation');
    cmdFactory.addMapAction(this.activateMediaAction$, 'activateMedia');
    cmdFactory.addMapAction(this.verwijderNotitiesAction$, 'verwijderNotitie');
    cmdFactory.addMapAction(this.bepaalStandpuntAction$, 'bepaalStandapunt');
    cmdFactory.addMapAction(this.closeStandpuntAction$, 'closeStandpunt');
    cmdFactory.addMapAction(this.getBoaActivityAction$, 'getBoaActivityAction$');
    this.vm$ = cmdFactory.merge(emptyVm);
  }

  private retrieveAgendapuntDataAction$ = this.route.data.pipe(
    map(data => data.viewData),
    map((arg: IAgendapuntViewResult) => (vm: IDetailPuntVm) => {
      return {
        ...vm,
        bespreken: arg.bespreken,
        ...arg.agendapunt,
        streamingLink: this.sanitizer.bypassSecurityTrustResourceUrl(arg.agendapunt.detail.StreamingLink),
        agendapuntIds: arg.agendapuntIds
      } as IDetailPuntVm;
    })
  );

  private openDocFromDocIdParamAction$ = this.retrieveAgendapuntDataAction$.pipe(
    mergeMap(_ => this.route.paramMap),
    filter(paramMap => paramMap.has('docid')),
    map(paramMap => +paramMap.get('docid')),
    delay(0), // vermijd dat deze observable te vroeg afgaat
    map(docid => (vm: IDetailPuntVm) => {
      const document = vm.detail.Documenten.find(d => d.Id === docid);
      this.navigateToDocument(document);
      return vm;
    })
  );

  // TODO mogelijk optimalistatie nodig, fragment > '' en alleen bij eerste retrieve
  private jumpToAnchorFromFragmentAction$ = this.route.fragment.pipe(
    map(fragment => (vm: IDetailPuntVm) => {
      jumpToAnchor(fragment);
      return vm;
    })
  );

  private refreshDataFromAppServiceAction$ = this.appService.refresh$.pipe(
    switchMap(_ => this.appService.refreshCurrentRoute(this.route, this.router)),
    map(_ => (vm: IDetailPuntVm) => vm)
  );

  private toggleBookmarkAction$ = this.toggleBookmarkSubj.pipe(
    mergeMap(punt => this.agendapuntService.toggleBookmark(punt.Id, punt.BladwijzerId)),
    map(bladwijzerId => (vm: IDetailPuntVm) => {
      state2.removeSubKey('detailzittingdata', vm.agendapunt.ZittingId);
      return {
        ...vm,
        agendapunt: {
          ...vm.agendapunt,
          BladwijzerId: bladwijzerId
        }
      };
    })
  );

  private verwijderNotitiesAction$ = this.verwijderNotitieSubj.pipe(
    mergeMap(notitie => this.agendapuntService.verwijderNotitie(notitie.NotitieId)),
    withLatestFrom(this.verwijderNotitieSubj),
    map(([_, notitie]) => (vm: IDetailPuntVm) => {
      return { ...vm, detail: { ...vm.detail, Notities: vm.detail.Notities.filter(n => n !== notitie) } };
    })
  );

  private closeStandpuntAction$ = this.closeStandpuntSubj.pipe(
    mergeMap(vm => this.agendapuntService.getDetailAgendapunt(vm.agendapunt.ZittingId, vm.agendapunt.Id)),
    map(agendapuntResult => (vm: IDetailPuntVm) => ({
      ...vm,
      standpunten: null,
      agendapunt: agendapuntResult.agendapunt,
      detail: agendapuntResult.detail
    }))
  );

  private bepaalStandpuntAction$ = this.bepaalStandpuntSubj.pipe(
    filter(punt => punt.StandpuntToegelaten),
    mergeMap(punt => this.standpuntService.getStandpunten(punt.Id)),
    map(standpunten => (vm: IDetailPuntVm) => ({
      ...vm, standpunten
    }))
  );

  ngOnInit() {
  }

  public getDocumentAgendapuntId = (_: number, documentAgendapunt: IDocumentDb) => documentAgendapunt.Id;
  public getGekoppeldPuntId = (_: number, punt: IReferencedPuntDb) => punt.Id;
  public getDossierId = (_: number, dossier: IDossierDb) => dossier.DossierId;
  public getNotitieId = (_: number, notitie: INotitieDb) => notitie.Id;
  public getSubpuntId = (_: number, subpunt: ISubpuntDb) => subpunt.Id;

  private puntNavigationAction$ = this.puntNavigationSubj.pipe(
    map(delta => (vm: IDetailPuntVm) => {
      const index = vm.agendapuntIds.findIndex(id => id === vm.agendapunt.Id);
      const nextIndex = delta === 1
        ? index + 1 >= vm.agendapuntIds.length ? 0 : index + 1
        : index - 1 < 0 ? vm.agendapuntIds.length - 1 : index - 1;

      const nextId = vm.agendapuntIds[nextIndex];
      this.appService.routing.navigateToDetailPuntReplaceHistory('Naar de zitting', vm.agendapunt.ZittingId, nextId, vm.bespreken);
      return vm;
    })
  );

  private activateMediaAction$ = this.activateMediaSubj.pipe(
    map(mediaActive => (vm: IDetailPuntVm) => ({ ...vm, mediaActive }))
  );

  private getBoaActivityAction$ = this.getBoaActivitySubj.pipe(
    switchMap(id => this.agendapuntService.getBoaDetail(id)),
    map(boaActivity => (vm: IDetailPuntVm) => ({ ...vm, boaActivity }))
  );

  navigateToNotitieToevoegen = (agendapunt: IAgendapuntDb) => {
    this.appService.routing.navigateToNotitieToevoegen(agendapunt.ZittingId, agendapunt.Id);
  }

  navigateToWijzigNotitie = (agendapunt: IAgendapuntDb, notitie: INotitieDb) => {
    this.appService.routing.navgateToWijzigNotitie(agendapunt.ZittingId, agendapunt.Id, notitie.NotitieId);
  }
  navigateToReageerNotitie = (agendapunt: IAgendapuntDb, notitie: INotitieDb) => {
    this.appService.routing.navigateToReageerNotitie(agendapunt.ZittingId, agendapunt.Id, notitie.NotitieId);
  }
  navigateToDocument(document: IDocument) {
    this.menuService.openDocument('agendapunt', document.Id, document.Titel, document.Verwijzing);
  }

  navigateToDossier(dossier: IDossierDb) {
    if (dossier.SoortDossierCode === 'BOA') {
      this.appService.routing.navigateToBoaDetail(dossier.DossierId);
      return;
    }
    window.open(dossier.UrlTemplate.replace('{0}', dossier.DossierId), '_blank');
  }
  navigateToRelatedPunt(relatedPunt: IReferencedPuntDb) {
    this.appService.routing.navigateToDetailPunt(relatedPunt.ZittingId, relatedPunt.RelatedPuntId);
    // moet na navigate omdat anders in navigationEnd verkeerde bijgewerkt wordt.
    this.appService.routing.createCurrentHistoryItem('Agendapunt');
  }

}
